/* components/patterns/ExpandableList.jsx — «первые N + показать все».

   Для перечней, которые читают СВЕРХУ: рейтинги, легенды донатов и графиков,
   «Вклад в рост». У такого списка смотрят верх — кто первый, чья доля больше, — а
   хвост из тридцати строк только растягивает блок и выталкивает соседей за экран.
   Поэтому список стоит свёрнутым до первых десяти, и «показать все» — осознанное
   действие. Обобщение свёрнутого ТОПа «Персонала» (TopList в
   screens/analytics/rowWindow.jsx — теперь тонкая обёртка над этим компонентом).

   Таблица-реестр — другой предмет: её листают, и там подгрузка при прокрутке
   (useRowWindow), а не разворот.

   Правила (контракт work/analytics-pack-oct/contracts/ui-components.md, раздел 1):
   • кнопка появляется, только если прячется БОЛЬШЕ двух строк: разворот ради двух —
     шум, а не помощь;
   • на кнопке ОБЩЕЕ число («Показать все — 30 категорий»), а не «ещё 20»: оно
     отвечает на вопрос «сколько их вообще», который и возникает у свёрнутого списка;
   • в свёрнутом виде список может нести строку-итог по спрятанным строкам
     (`summary`) — чтобы видимые и «остальные» сходились с итогом глазами;
   • кнопка — настоящая <button> с aria-expanded: Enter и Пробел работают сами.

   Кнопка стоит ПОСЛЕ списка, а не внутри: списки бывают гридом со строками
   `display: contents` (рейтинги «Персонала», «Вклад в рост»), и кнопка внутри
   стала бы ещё одной ячейкой грида. Строка-итог, наоборот, — внутри: она такая же
   строка, как остальные, и разметку ей даёт потребитель (для <ul> — <li>). */
import React from 'react';
import { Icon } from '../ui/Icon.jsx';

const { useState, useCallback } = React;

/** Сколько строк показывать в свёрнутом виде. */
export const EXPAND_LIMIT = 10;

/**
 * Чистое правило окна.
 * total — строк всего; limit — сколько показывать свёрнутым; open — нажат ли разворот.
 * → { collapsible, open, shown }: есть ли что сворачивать, раскрыт ли список,
 *   сколько строк рисовать. «Раскрыто» без чего раскрывать не бывает.
 */
export function expandState(total, limit = EXPAND_LIMIT, open = false) {
  const n = Math.max(0, Number(total) || 0);
  const lim = Math.max(1, Number(limit) || EXPAND_LIMIT);
  const collapsible = n > lim + 2;
  const isOpen = collapsible && !!open;
  return { collapsible, open: isOpen, shown: (isOpen || !collapsible) ? n : lim };
}

/** Тот же разворот для списков со своей разметкой (легенда графика и т. п.). */
export function useExpandable(total, limit = EXPAND_LIMIT) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const st = expandState(total, limit, open);
  return { shown: st.shown, open: st.open, toggle, collapsible: st.collapsible };
}

const unitOf = (unit, n) => (typeof unit === 'function' ? unit(n) : (unit || ''));

/* Свернули длинный список, стоя внизу, — кнопка уезжает вверх вместе с концом
   списка, и человек остаётся смотреть на чужой блок далеко ниже. Возвращаем кнопку
   в кадр (ровно настолько, насколько нужно). */
function keepInView(el) {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  const go = () => el.scrollIntoView({ block: 'nearest' });
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(go); else go();
}

/* Разметка кнопки — функцией, а не только компонентом: ExpandableList вставляет её
   прямо в своё дерево (одна разметка на оба пути). */
function expandButton({ open, total, limit = EXPAND_LIMIT, unit, onToggle,
  className = 'expand-list__more', moreLabel = 'Показать все' }) {
  const u = unitOf(unit, total);
  return (
    <button type="button" className={className} aria-expanded={open}
      onClick={(e) => {
        const el = e && e.currentTarget;
        onToggle();
        if (open) keepInView(el);
      }}>
      <Icon name={open ? 'chevron-up' : 'chevron-down'} size={13} />
      {/* Текст — ОДНИМ элементом: кнопка — inline-flex, и без обёртки каждый
          кусок («Показать все —», число, единица) становился отдельным
          флекс-элементом — в узкой колонке они переносились порознь и тире
          отрывалось от числа. */}
      <span>
        {open
          ? <>Свернуть до первых <span className="num">{limit}</span></>
          : <>{moreLabel} — <span className="num">{total}</span>{u ? ' ' + u : ''}</>}
      </span>
    </button>
  );
}

/**
 * Кнопка разворота для списка со своей разметкой: useExpandable + эта кнопка.
 * moreLabel — глагол свёрнутого вида («Показать все»; рейтингу людей — «Показать всех»).
 */
export function ExpandToggle(props) {
  return expandButton(props);
}

/**
 * items          — строки;
 * render(item,i) — разметка строки (с key), i — индекс во ВСЁМ списке;
 * limit          — сколько показывать свёрнутым (10);
 * unit           — подпись числа на кнопке: строка или (n) => строка;
 * listClassName, as — класс и тег списка ('div' по умолчанию; для <ul> строки — <li>);
 * summary(rest)  — строка-итог по спрятанным строкам (только в свёрнутом виде);
 * moreLabel, buttonClassName — глагол и класс кнопки (для обёрток со своим видом).
 */
export function ExpandableList({ items, render, limit = EXPAND_LIMIT, unit, listClassName,
  as = 'div', summary, moreLabel, buttonClassName }) {
  const rows = items || [];
  const { shown, open, toggle, collapsible } = useExpandable(rows.length, limit);
  const List = as;
  const rest = collapsible && !open ? rows.slice(shown) : null;
  return (
    <>
      <List className={listClassName}>
        {rows.slice(0, shown).map(render)}
        {rest && summary ? summary(rest) : null}
      </List>
      {collapsible && expandButton({
        open, total: rows.length, limit, unit, onToggle: toggle, moreLabel, className: buttonClassName,
      })}
    </>
  );
}
